import * as React from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/16/solid'
import { Button, ButtonText } from '@/components/ui/button'
import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { Skeleton } from '@/components/ui/skeleton'
import useNumberFormatter from '@/hooks/useNumberFormatter'
import { cn } from '@/lib/utils'
import BottomSheet from '@/mobile/sheets/BottomSheet'
import useMobileTable from './useMobileTable'
import { PhonePeriodControls } from './PeriodControls'
import VaultScopeList from './VaultScopeList'
import {
  amountClass,
  discrepancyIsGood,
  isActiveTarget,
  rowBackground,
  targetPath,
} from './rowStyle'
import type { TableRowModel } from './tableRows'

const WHOLE = { minimumFractionDigits: 0, maximumFractionDigits: 0 }
const DISCREPANCY = { ...WHOLE, addParanthesesForNegative: false }

export default function PhoneTable() {
  const table = useMobileTable({ visible: 1 })
  const navigate = useNavigate()
  const location = useLocation()
  const [vaultsOpen, setVaultsOpen] = React.useState(false)

  const money = useNumberFormatter(WHOLE)
  const money2 = useNumberFormatter({})
  const diffFormat = useNumberFormatter(DISCREPANCY)

  const offset = table.offsets[0]
  const closing = table.rows.find((r) => r.key === 'closing')?.cells[0]
  const net = table.rows.find((r) => r.key === 'net')?.cells[0]
  const hasActuals = offset !== undefined && offset <= 0

  const onRow = (row: TableRowModel) => {
    if (row.expanded !== undefined) table.toggleRow(row.key)
    else if (row.target) navigate(targetPath(row.target, offset))
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col bg-white">
      <div className="flex shrink-0 flex-col gap-2.5 px-4 py-3">
        <PhonePeriodControls table={table} />
        <Card className="w-full rounded-2xl">
          <div className="flex flex-col gap-1.5 px-4 pb-[15px] pt-[13px]">
            <div className="flex items-center justify-between gap-2.5">
              <span className="text-[10px] font-semibold tracking-[.1em] text-txt-placeholder">
                CLOSING BALANCE
              </span>
              <Button
                variant="outline"
                size="sm"
                dropdown
                className="rounded-lg"
                onClick={() => setVaultsOpen(true)}
              >
                <ButtonText>{table.scope.label}</ButtonText>
              </Button>
            </div>
            <div className="flex items-baseline justify-between gap-3">
              {table.isLoading ? (
                <Skeleton className="h-8 w-40" />
              ) : (
                <span className="text-[27px] font-bold tabular-nums tracking-[-.02em] text-txt-primary">
                  {money2(closing?.amount ?? 0)}
                </span>
              )}
              {hasActuals ? (
                <span className="flex items-baseline gap-1.5">
                  <span className="text-[10px] font-semibold tracking-[.1em] text-txt-placeholder">
                    NET
                  </span>
                  <span
                    className={cn(
                      'text-base font-bold tabular-nums',
                      (net?.amount ?? 0) < 0
                        ? 'text-txt-error'
                        : 'text-txt-success',
                    )}
                  >
                    {(net?.amount ?? 0) < 0 ? '−' : '+'}
                    {diffFormat(Math.abs(net?.amount ?? 0))}
                  </span>
                </span>
              ) : (
                <span className="text-[12.5px] font-semibold text-txt-placeholder">
                  Estimate
                </span>
              )}
            </div>
          </div>
        </Card>
      </div>

      <div className="pf-mobile-scroll min-h-0 flex-1 overflow-y-auto px-4 pb-8">
        {table.isLoading ? (
          <div className="flex flex-col gap-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <Skeleton key={i} className="h-11 w-full" />
            ))}
          </div>
        ) : (
          table.rows.map((row, i) => (
            <PhoneRow
              key={row.key}
              row={row}
              first={i === 0}
              active={isActiveTarget(
                row.target,
                offset,
                location.pathname,
                location.search,
              )}
              money={money}
              diffFormat={diffFormat}
              onClick={() => onRow(row)}
            />
          ))
        )}
      </div>

      <BottomSheet
        open={vaultsOpen}
        onClose={() => setVaultsOpen(false)}
        label="Vaults"
        className="px-[18px]"
      >
        <div className="flex items-center justify-between gap-3 pb-2 pt-3">
          <div className="flex flex-col gap-0.5">
            <span className="text-[19px] font-bold text-txt-primary">
              Vaults
            </span>
            <span className="text-xs text-txt-secondary">
              Scopes the vault rows and the closing balance
            </span>
          </div>
          <button
            type="button"
            className="text-[12.5px] font-semibold text-txt-accent"
            onClick={table.scope.toggleAll}
          >
            {table.scope.allSelected ? 'Clear all' : 'Select all'}
          </button>
        </div>
        <VaultScopeList table={table} />
        <div className="flex gap-2.5 pt-3.5">
          <Button
            size="lg"
            className="flex-1 rounded-xl"
            onClick={() => setVaultsOpen(false)}
          >
            Show {table.scope.label}
          </Button>
          <Button
            size="lg"
            variant="outline"
            className="rounded-xl"
            onClick={() => {
              setVaultsOpen(false)
              navigate('/vaults/new')
            }}
          >
            ＋ New vault
          </Button>
        </div>
      </BottomSheet>
    </div>
  )
}

function PhoneRow({
  row,
  first,
  active,
  money,
  diffFormat,
  onClick,
}: {
  row: TableRowModel
  first: boolean
  active: boolean
  money: (n: number) => string
  diffFormat: (n: number) => string
  onClick: () => void
}) {
  const cell = row.cells[0]
  const isCap = row.kind === 'cap' || row.kind === 'net' || row.kind === 'closing'
  const interactive = row.expanded !== undefined || !!row.target

  return (
    <div
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      onClick={interactive ? onClick : undefined}
      onKeyDown={(e) => {
        if (interactive && (e.key === 'Enter' || e.key === ' ')) {
          e.preventDefault()
          onClick()
        }
      }}
      aria-expanded={row.expanded}
      className={cn(
        'flex items-center justify-between gap-3 pr-3',
        rowBackground(row),
        interactive && 'cursor-pointer active:brightness-95',
        active && 'bg-accent-tr-08 ring-1 ring-inset ring-[hsl(var(--border-accent))]',
        isCap
          ? cn(
              'rounded-[10px] pl-2.5',
              row.kind === 'closing' ? 'h-[46px]' : row.kind === 'net' ? 'mt-4 h-11' : 'h-[34px]',
              row.kind === 'cap' && !first && 'mt-4',
            )
          : cn(
              'border-b border-light',
              row.kind === 'group' && 'h-[42px] pl-3',
              row.kind === 'leaf' && 'h-[52px]',
              row.kind === 'leaf' && (row.depth > 0 ? 'pl-[30px]' : 'pl-3'),
              row.kind === 'add' && 'h-11 pl-3',
            ),
      )}
    >
      <div className="flex min-w-0 items-center gap-[7px]">
        {row.expanded !== undefined &&
          (row.expanded ? (
            <ChevronDownIcon className="size-3 shrink-0 text-txt-placeholder" />
          ) : (
            <ChevronRightIcon className="size-3 shrink-0 text-txt-placeholder" />
          ))}
        <div className="flex min-w-0 flex-col gap-px">
          <span
            className={cn(
              'truncate',
              isCap &&
                'text-[11px] font-medium uppercase tracking-[.09em] text-txt-secondary',
              row.kind === 'closing' && 'text-txt-primary',
              row.kind === 'group' && 'text-[13px] font-medium text-txt-primary',
              row.kind === 'leaf' && 'text-[14.5px] font-semibold text-txt-primary',
              row.kind === 'add' && 'text-[14.5px] font-semibold text-txt-accent',
            )}
          >
            {row.kind === 'add' ? `＋ ${row.name}` : row.name}
          </span>
          {row.meta && row.kind !== 'cap' && (
            <span className="truncate text-[11px] text-txt-placeholder">
              {row.meta}
            </span>
          )}
        </div>
      </div>

      {cell && (
        <div className="flex shrink-0 items-center gap-2">
          {cell.discrepancy !== null && (
            <Badge
              className="rounded-md tabular-nums"
              variant={
                discrepancyIsGood(row, cell.discrepancy)
                  ? 'secondary'
                  : 'destructive'
              }
            >
              {cell.discrepancy > 0 ? '+' : '−'}
              {diffFormat(Math.abs(cell.discrepancy))}
            </Badge>
          )}
          <span
            className={cn(
              'rounded-md font-semibold tabular-nums',
              isCap ? 'text-[13.5px]' : row.kind === 'group' ? 'text-sm' : 'text-[15px]',
              amountClass(row, cell),
              cell.negative && 'bg-yellow-lowest px-1.5',
            )}
          >
            {cell.amount === null || cell.amount === 0
              ? '—'
              : money(cell.amount)}
          </span>
        </div>
      )}
    </div>
  )
}
